import { useCallback, useRef, useState } from "react";

/**
 * Game clock that excludes wall-clock time spent paused (and time between
 * resume and the player's first action). Use `gameNow()` instead of
 * `Date.now()` for round/step deadlines and session timers.
 */
export function useGamePause() {
  const [paused, setPaused] = useState(false);
  const [timerHeld, setTimerHeld] = useState(false);
  const pausedRef = useRef(false);
  const timerHeldRef = useRef(false);
  pausedRef.current = paused;
  timerHeldRef.current = timerHeld;
  const pauseOffsetMs = useRef(0);
  const pausedAtRef = useRef<number | null>(null);

  const timerFrozen = paused || timerHeld;

  const isTimerFrozen = useCallback((): boolean => {
    return pausedRef.current || timerHeldRef.current;
  }, []);

  const applyPauseOffset = useCallback(() => {
    if (pausedAtRef.current != null) {
      pauseOffsetMs.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }
  }, []);

  // Stable reference: must not change when pause state toggles, or effects
  // that list `gameNow` in deps (e.g. session start) re-run and reset timers.
  const gameNow = useCallback((): number => {
    const frozen = pausedRef.current || timerHeldRef.current;
    if (frozen && pausedAtRef.current != null) {
      return pausedAtRef.current - pauseOffsetMs.current;
    }
    return Date.now() - pauseOffsetMs.current;
  }, []);

  const togglePause = useCallback(() => {
    setPaused((wasPaused) => {
      if (!wasPaused) {
        if (pausedAtRef.current == null) {
          pausedAtRef.current = Date.now();
        }
        pausedRef.current = true;
        timerHeldRef.current = false;
        setTimerHeld(false);
        return true;
      }
      // Resume gameplay; hold timer until the player acts.
      pausedRef.current = false;
      timerHeldRef.current = true;
      setTimerHeld(true);
      return false;
    });
  }, []);

  /**
   * Call when the player acts (tap, answer, etc.). Unfreezes the clock and
   * clears both full pause and post-resume hold.
   */
  const resumeTimerOnPlay = useCallback(() => {
    if (!pausedRef.current && !timerHeldRef.current) return;
    applyPauseOffset();
    if (pausedRef.current) {
      pausedRef.current = false;
      setPaused(false);
    }
    if (timerHeldRef.current) {
      timerHeldRef.current = false;
      setTimerHeld(false);
    }
  }, [applyPauseOffset]);

  return {
    paused,
    timerFrozen,
    togglePause,
    resumeTimerOnPlay,
    isTimerFrozen,
    gameNow,
  };
}
